import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../lib/api';
import { ownerViewingSlotsAPI } from '../../lib/ownerViewingSlotsApi';
import { translateTemplate } from '../../lib/publicListingTruth';
import {
  canOfferViewingSlotsForInquiryStatus,
  extractApiErrorMessage,
  formatSlotDateTime,
  validateOwnerViewingSlotForm,
} from '../../lib/ownerViewingSlotForm';
import {
  LOCATION_TYPE_OPTIONS,
  SUGGESTED_VIEWING_TIMEZONES,
  VIEWING_DURATION_OPTIONS,
  buildEmptyViewingSlotForm,
  buildViewingSchedulingConstraints,
  buildViewingSchedulingConstraintsFromCalendar,
  buildViewingSlotApiBody,
  extractListingIdFromInquiry,
  findNextValidViewingDate,
  formatViewingDateLabel,
  formatViewingEndTimePreview,
  getMeetingLocationOptionsForType,
  isCustomMeetingLocationOption,
  isViewingDateBlocked,
  resolveInitialViewingFormDate,
} from '../../lib/ownerViewingSlotDisplay';
import {
  ADD_VIEWING_SLOT_BTN_TEST_ID,
  OFFER_VIEWING_DATES_SECTION_TEST_ID,
} from '../../lib/ownerViewingSlotDiscoverability';

const SLOT_STATUS_STYLES = {
  offered: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  reserved: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  confirmed: 'bg-signal-blue/15 text-signal-blue border-signal-blue/30',
  cancelled: 'bg-slate-500/15 text-slate-600 border-slate-500/30',
  expired: 'bg-slate-400/15 text-slate-500 border-slate-400/30',
};

function slotStatusClass(status) {
  return SLOT_STATUS_STYLES[String(status || '').toLowerCase()] || SLOT_STATUS_STYLES.offered;
}

export default function OwnerInquiryViewingSlots({ inquiry }) {
  const { t, locale } = useLanguage();
  const inquiryId = inquiry?.id;
  const inquiryStatus = inquiry?.status;
  const listingId = extractListingIdFromInquiry(inquiry);

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => buildEmptyViewingSlotForm(inquiry));
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [schedulingConstraints, setSchedulingConstraints] = useState(() => buildViewingSchedulingConstraints());
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [dateManuallySet, setDateManuallySet] = useState(false);

  const canOffer = canOfferViewingSlotsForInquiryStatus(inquiryStatus);

  const meetingLocationOptions = useMemo(
    () => getMeetingLocationOptionsForType(form.locationType),
    [form.locationType],
  );

  const endTimePreview = useMemo(
    () =>
      formatViewingEndTimePreview({
        date: form.date,
        startTime: form.startTime,
        durationMinutes: form.durationMinutes,
      }),
    [form.date, form.startTime, form.durationMinutes],
  );

  const timezoneSuggestions = useMemo(() => {
    const values = new Set(SUGGESTED_VIEWING_TIMEZONES);
    if (form.timezone?.trim()) {
      values.add(form.timezone.trim());
    }
    return Array.from(values);
  }, [form.timezone]);

  const nextValidDate = useMemo(() => {
    if (!schedulingConstraints) {
      return '';
    }
    return findNextValidViewingDate({
      startDateStr: form.date || schedulingConstraints.minDate,
      constraints: schedulingConstraints,
    });
  }, [form.date, schedulingConstraints]);

  const currentDateBlocked = useMemo(() => {
    if (!form.date || !schedulingConstraints) {
      return null;
    }
    return isViewingDateBlocked(form.date, schedulingConstraints);
  }, [form.date, schedulingConstraints]);

  const loadSchedulingConstraints = useCallback(async () => {
    if (!listingId) {
      const fallback = buildViewingSchedulingConstraints();
      setSchedulingConstraints(fallback);
      return fallback;
    }

    setCalendarLoading(true);
    try {
      const res = await api.get(`/bookings/listings/${listingId}/calendar`, {
        params: { months: 6 },
      });
      const constraints = buildViewingSchedulingConstraintsFromCalendar(res.data);
      setSchedulingConstraints(constraints);
      return constraints;
    } catch (error) {
      console.error('Failed to load listing calendar for viewing slots:', error);
      const fallback = buildViewingSchedulingConstraints();
      setSchedulingConstraints(fallback);
      return fallback;
    } finally {
      setCalendarLoading(false);
    }
  }, [listingId]);

  const loadSlots = useCallback(async () => {
    if (!inquiryId) {
      setSlots([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await ownerViewingSlotsAPI.list(inquiryId);
      setSlots(res.data?.slots || []);
    } catch (error) {
      console.error('Failed to load viewing slots:', error);
      toast.error(
        extractApiErrorMessage(error, t('owner.viewing_load_failed', 'Failed to load viewing slots')),
      );
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [inquiryId, t]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const openForm = async () => {
    setFormErrors({});
    setDateManuallySet(false);
    setShowForm(true);

    const constraints = await loadSchedulingConstraints();
    const initialDate = resolveInitialViewingFormDate({
      preferredDate: '',
      constraints,
      dateManuallySet: false,
    });
    setForm({
      ...buildEmptyViewingSlotForm(inquiry, constraints),
      date: initialDate,
    });
  };

  const handleDateChange = (nextDate) => {
    setDateManuallySet(true);
    setForm((prev) => ({ ...prev, date: nextDate }));
    setFormErrors((prev) => {
      if (!prev.date) {
        return prev;
      }
      const { date, ...rest } = prev;
      return rest;
    });
  };

  const handleUseNextValidDate = () => {
    if (!nextValidDate) {
      return;
    }
    setDateManuallySet(true);
    setForm((prev) => ({ ...prev, date: nextValidDate }));
    setFormErrors((prev) => {
      if (!prev.date) {
        return prev;
      }
      const { date, ...rest } = prev;
      return rest;
    });
  };

  const handleLocationTypeChange = (locationType) => {
    setForm((prev) => ({
      ...prev,
      locationType,
      meetingLocationOption: '',
      meetingLocationCustom: '',
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validateOwnerViewingSlotForm(form, t, { schedulingConstraints });
    setFormErrors(validation.errors);
    if (!validation.valid) {
      return;
    }

    setSubmitting(true);
    try {
      const body = buildViewingSlotApiBody(form, validation.payload, t);
      await ownerViewingSlotsAPI.create(inquiryId, body);
      toast.success(t('owner.viewing_slot_created', 'Viewing slot created'));
      setForm(buildEmptyViewingSlotForm(inquiry));
      setShowForm(false);
      setFormErrors({});
      await loadSlots();
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error, t('owner.viewing_create_failed', 'Failed to create viewing slot')),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSlot = async (slot) => {
    if (slot.status === 'cancelled') {
      return;
    }
    const confirmed = window.confirm(
      t('owner.viewing_cancel_confirm', 'Cancel this viewing slot? Guests will no longer be able to reserve it.'),
    );
    if (!confirmed) {
      return;
    }

    setCancellingId(slot.id);
    try {
      await ownerViewingSlotsAPI.cancel(slot.id);
      toast.success(t('owner.viewing_slot_cancelled', 'Viewing slot cancelled'));
      await loadSlots();
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error, t('owner.viewing_cancel_failed', 'Failed to cancel viewing slot')),
      );
    } finally {
      setCancellingId(null);
    }
  };

  if (!canOffer) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`viewing-slots-${inquiryId}`}>
        <p className="text-xs text-slate-500">
          {t('owner.viewing_not_available_status', 'Viewing slots cannot be offered for this inquiry status.')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="mt-4 rounded-lg border-2 border-signal-blue/25 bg-signal-blue/5 p-3"
      data-testid={`viewing-slots-${inquiryId}`}
    >
      <div data-testid={OFFER_VIEWING_DATES_SECTION_TEST_ID}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-signal-blue shrink-0" aria-hidden />
              {t('owner.viewing_offer_title', 'Offer viewing dates')}
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              {t('owner.viewing_offer_subtitle', 'Propose viewing times for this guest')}
            </p>
            <p className="text-xs text-slate-500 mt-1.5 leading-snug">
              {t(
                'owner.viewing_offer_helper',
                'Viewing slots are offered per booking request. Add one or more times the guest can reserve.',
              )}
            </p>
          </div>
          {!showForm && (
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs bg-signal-blue hover:bg-signal-blue-hover text-white shrink-0"
              onClick={openForm}
              data-testid={ADD_VIEWING_SLOT_BTN_TEST_ID}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              {t('owner.viewing_add_slot', 'Add viewing slot')}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t('common.loading', 'Loading...')}
        </div>
      ) : slots.length === 0 ? (
        <div className="mb-2" data-testid="viewing-slots-empty">
          <p className="text-xs text-slate-600 mb-2">
            {t('owner.viewing_slots_empty', 'No viewing slots offered yet')}
          </p>
          {!showForm && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs border-signal-blue/40 text-signal-blue"
              onClick={openForm}
            >
              <Plus className="w-3 h-3 mr-1" />
              {t('owner.viewing_add_slot', 'Add viewing slot')}
            </Button>
          )}
        </div>
      ) : (
        <ul className="space-y-2 mb-2">
          {slots.map((slot) => (
            <li
              key={slot.id}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
              data-testid={`viewing-slot-${slot.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">
                    {formatSlotDateTime(slot.start_time)} – {formatSlotDateTime(slot.end_time)}
                  </p>
                  <p className="text-slate-500 mt-0.5">
                    {t('owner.viewing_timezone', 'Timezone')}: {slot.timezone || 'Europe/Berlin'}
                    {' · '}
                    {t('owner.viewing_capacity', 'Capacity')}: {slot.capacity}
                    {' · '}
                    {t('owner.viewing_reserved', 'Reserved')}: {slot.reserved_count ?? 0}
                  </p>
                  {(slot.created_at || slot.updated_at) && (
                    <p className="text-slate-400 mt-0.5">
                      {slot.updated_at && slot.updated_at !== slot.created_at
                        ? `${t('owner.viewing_updated', 'Updated')}: ${formatSlotDateTime(slot.updated_at)}`
                        : `${t('owner.viewing_created', 'Created')}: ${formatSlotDateTime(slot.created_at)}`}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge className={`text-[10px] border ${slotStatusClass(slot.status)}`}>
                    {slot.status}
                  </Badge>
                  {slot.status !== 'cancelled' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50 px-2"
                      disabled={cancellingId === slot.id}
                      onClick={() => handleCancelSlot(slot)}
                      data-testid={`cancel-slot-${slot.id}`}
                    >
                      {cancellingId === slot.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        t('owner.viewing_cancel_slot', 'Cancel slot')
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="viewing-slot-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_date', 'Date')}
              </label>
              <input
                type="date"
                value={form.date}
                min={schedulingConstraints?.minDate}
                disabled={calendarLoading}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 disabled:bg-slate-50 disabled:text-slate-400"
                data-testid="viewing-slot-date-input"
              />
              <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                {t(
                  'owner.viewing_date_blocked_helper',
                  'Past dates, rental periods, and unavailable listing dates cannot be selected.',
                )}
              </p>
              {calendarLoading && (
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {t('owner.viewing_date_loading', 'Loading available dates...')}
                </p>
              )}
              {!calendarLoading && nextValidDate && currentDateBlocked?.blocked && (
                <div className="mt-1 space-y-1">
                  <p className="text-[10px] text-amber-700" data-testid="viewing-next-valid-date-hint">
                    {translateTemplate(
                      t('owner.viewing_next_available_date', 'Next available date: {date}'),
                      { date: formatViewingDateLabel(nextValidDate, locale) },
                    )}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 text-[10px] px-2"
                    onClick={handleUseNextValidDate}
                    data-testid="viewing-use-next-valid-date"
                  >
                    {t('owner.viewing_use_next_available_date', 'Use next available date')}
                  </Button>
                </div>
              )}
              {!calendarLoading && nextValidDate && !currentDateBlocked?.blocked && form.date && form.date !== schedulingConstraints?.minDate && (
                <p className="text-[10px] text-slate-500 mt-0.5" data-testid="viewing-selected-date-ok">
                  {translateTemplate(
                    t('owner.viewing_selected_date', 'Selected date: {date}'),
                    { date: formatViewingDateLabel(form.date, locale) },
                  )}
                </p>
              )}
              {formErrors.date && (
                <p className="text-[10px] text-red-600 mt-0.5">{formErrors.date}</p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_start_time', 'Start time')}
              </label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800"
              />
              {formErrors.startTime && (
                <p className="text-[10px] text-red-600 mt-0.5">{formErrors.startTime}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_duration', 'Duration')}
              </label>
              <select
                value={form.durationMinutes}
                onChange={(e) => setForm((prev) => ({ ...prev, durationMinutes: e.target.value }))}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 bg-white"
              >
                {VIEWING_DURATION_OPTIONS.map((minutes) => (
                  <option key={minutes} value={String(minutes)}>
                    {t(`owner.viewing_duration_${minutes}`, `${minutes} minutes`)}
                  </option>
                ))}
              </select>
              {formErrors.durationMinutes && (
                <p className="text-[10px] text-red-600 mt-0.5">{formErrors.durationMinutes}</p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_end_time_preview', 'End time (calculated)')}
              </label>
              <p
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-slate-50 min-h-[30px] flex items-center"
                data-testid="viewing-end-time-preview"
              >
                {endTimePreview || t('owner.viewing_end_time_preview_empty', 'Select date, start time, and duration')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_timezone', 'Timezone')}
              </label>
              <input
                type="text"
                list="viewing-timezone-suggestions"
                value={form.timezone}
                onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800"
              />
              <datalist id="viewing-timezone-suggestions">
                {timezoneSuggestions.map((zone) => (
                  <option key={zone} value={zone} />
                ))}
              </datalist>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {t(
                  'owner.viewing_timezone_hint',
                  'Defaults to listing timezone, your browser timezone, or Europe/Berlin. You can override.',
                )}
              </p>
              {formErrors.timezone && (
                <p className="text-[10px] text-red-600 mt-0.5">{formErrors.timezone}</p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_capacity', 'Capacity')}
              </label>
              <input
                type="number"
                min="1"
                value={form.capacity}
                onChange={(e) => setForm((prev) => ({ ...prev, capacity: e.target.value }))}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800"
              />
              {formErrors.capacity && (
                <p className="text-[10px] text-red-600 mt-0.5">{formErrors.capacity}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_location_type', 'Location type (optional)')}
              </label>
              <select
                value={form.locationType}
                onChange={(e) => handleLocationTypeChange(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 bg-white"
              >
                <option value="">
                  {t('owner.viewing_location_type_select', 'Select location type')}
                </option>
                {LOCATION_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, option.value)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_meeting_location', 'Meeting location (optional)')}
              </label>
              <select
                value={form.meetingLocationOption}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    meetingLocationOption: e.target.value,
                    meetingLocationCustom: e.target.value === 'custom' ? prev.meetingLocationCustom : '',
                  }))
                }
                disabled={!form.locationType}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 bg-white disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">
                  {t('owner.viewing_meeting_location_select', 'Select meeting location')}
                </option>
                {meetingLocationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, option.value)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isCustomMeetingLocationOption(form.meetingLocationOption) && (
            <div>
              <label className="text-[10px] font-medium text-slate-600 block mb-1">
                {t('owner.viewing_meeting_location_custom', 'Custom meeting details')}
              </label>
              <input
                type="text"
                value={form.meetingLocationCustom}
                onChange={(e) => setForm((prev) => ({ ...prev, meetingLocationCustom: e.target.value }))}
                placeholder={t(
                  'owner.viewing_meeting_location_custom_ph',
                  'Add guidance without sharing a private address unless already intended',
                )}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800"
              />
            </div>
          )}

          <p className="text-[10px] text-slate-500 leading-snug">
            {t(
              'owner.viewing_future_calendar_note',
              'Calendar invite and map-link support will be added later.',
            )}
          </p>

          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={submitting}
              className="flex-1 bg-signal-blue hover:bg-signal-blue-hover text-white h-8 text-xs"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {t('owner.viewing_submit_slot', 'Create slot')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => {
                setShowForm(false);
                setFormErrors({});
              }}
            >
              <X className="w-3 h-3 mr-1" />
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
